import {
  ConversationMessage,
  ConversationParticipant,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
} from '@stripe-internal/conversations-client';
import {ApiMethods, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

type GetConversationMessagesHandlerDeps = {
  getConversation: (
    conversationId: string,
  ) => ReturnType<ApiMethods['getConversation']>;
  getParticipant: () => ConversationParticipant | undefined;
  reportWarning?: ReportWarning;
  enableUhPendingInput?: boolean;
  attachmentDownloadPath?: string;
  // Threads-only; see PollConversationDeps.includeTransitionContext.
  includeTransitionContext?: boolean;
};

export class GetConversationMessagesHandler {
  private deps: GetConversationMessagesHandlerDeps;

  constructor(deps: GetConversationMessagesHandlerDeps) {
    this.deps = deps;
  }

  call = async (
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse> => {
    if (!params.conversationId) {
      return {code: 400, error: 'Missing conversationId'};
    }

    const response = await this.deps.getConversation(params.conversationId);
    if (response.type === 'error') {
      if (response.code === 404) {
        return {code: 200, data: {messages: {}}};
      }
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const framework = FrameworkAdapter.fromApiConversation({
      apiConversation: response.data,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
      user: this.deps.getParticipant(),
      isActive: true,
      reportWarning: this.deps.reportWarning,
    });

    const allMessages = framework.messages;
    const messages: Record<string, ConversationMessage> = params.messageIds
      ? Object.fromEntries(
          params.messageIds
            .filter((id) => id in allMessages)
            .map((id) => [id, allMessages[id]]),
        )
      : allMessages;
    const {overview} = response.data;
    const transition = this.deps.includeTransitionContext
      ? FrameworkAdapter.deriveChannelContext(response.data.messages)
      : {};
    const context =
      response.data.id === params.conversationId
        ? {
            ...(overview?.id === params.conversationId
              ? {conversationOverview: overview}
              : {}),
            ...transition,
          }
        : {};

    return {
      code: 200,
      data: {messages, ...(Object.keys(context).length ? {context} : {})},
    };
  };
}
